// frontend/src/components/leads/LeadTable.js

import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import LeadStatusBadge from "./LeadStatusBadge";
import {
  getUserTimezone,
  formatDateInUserTimezone,
  formatUtcDateTimeParts,
} from "../../utils/timezone";
import api from "../../services/api";
import AddActivity from "../activities/AddActivity";
import NextActivityCell from "../nextActivities/NextActivityCell";
import AddNextActivity from "../nextActivities/AddNextActivity";
import SuspendPrompt from "../nextActivities/SuspendPrompt";

/* ============================ R6 (additive) — imports ============================ */
import RowContextMenu from "./RowContextMenu";
import RowActionPrompt from "./RowActionPrompt";
import QuickAddContactModal from "./QuickAddContactModal";
import { cycleSort } from "./LeadSortMenu";
/* ============================================================================== */

function LeadTable({
  leads,
  onBookDemo,
  onView,
  onEdit,
  onLeadQuickUpdated,
  onLeadProductsQuickUpdated,
  onAddActivity,
  onAddNextActivity,
  onCompleteNextActivity,
  onSuspendNextActivity,
  userRole,
  visibleColumns = {},
  productCatalog = [],
  /* R6 (additive) */
  sortState = [],
  onSortChange,
  selectionMode = false,
  selectedIds = new Set(),
  onSelectionChange,
}) {
  const [overlay, setOverlay] = useState(null);

  const longPressTimerRef = useRef(null);
  const touchStartPosRef = useRef({ x: 0, y: 0 });

  const [quickEditLeadId, setQuickEditLeadId] = useState(null);
  const [quickEditStatus, setQuickEditStatus] = useState("");
  const [statusOptions, setStatusOptions] = useState([]);
  const [selectableStatusOptions, setSelectableStatusOptions] = useState([]);
  const [isSavingStatus, setIsSavingStatus] = useState(false);
  const [statusError, setStatusError] = useState("");
  const [quickEditPosition, setQuickEditPosition] = useState(null);

  const [quickEditProductsLeadId, setQuickEditProductsLeadId] = useState(null);
  const [quickEditProducts, setQuickEditProducts] = useState([]);
  const [isSavingProducts, setIsSavingProducts] = useState(false);
  const [productsError, setProductsError] = useState("");
  const [productsQuickEditPosition, setProductsQuickEditPosition] =
    useState(null);

  const [activityLeadId, setActivityLeadId] = useState(null);
  const [isSavingActivity, setIsSavingActivity] = useState(false);

  const [nextActivityLeadId, setNextActivityLeadId] = useState(null);
  const [isSavingNextActivity, setIsSavingNextActivity] = useState(false);
  const [suspendTarget, setSuspendTarget] = useState(null);
  const [isSavingSuspend, setIsSavingSuspend] = useState(false);

  /* ============================ R6 (additive) — state ============================ */
  const [rowMenu, setRowMenu] = useState({
    open: false,
    x: 0,
    y: 0,
    lead: null,
  });
  const [quickAddContactLead, setQuickAddContactLead] = useState(null);

  // Row action prompt (Edit Source / Assign Rep)
  const [prompt, setPrompt] = useState({
    open: false,
    type: null, // "source" | "assign"
    lead: null,
    saving: false,
  });
  const [sourceOptions, setSourceOptions] = useState([]);
  const [repOptions, setRepOptions] = useState([]);
  /* ============================================================================== */

  const quickEditMenuRef = useRef(null);
  const productsQuickEditMenuRef = useRef(null);

  const openAddNextActivity = (leadId) => {
    setNextActivityLeadId(leadId);
  };

  const handleNextActivitySave = async (formData) => {
    if (!nextActivityLeadId || !onAddNextActivity) return;
    setIsSavingNextActivity(true);
    try {
      await onAddNextActivity(nextActivityLeadId, formData);
      setNextActivityLeadId(null);
    } finally {
      setIsSavingNextActivity(false);
    }
  };

  const handleNextActivityComplete = async (lead, nextActivity) => {
    if (!onCompleteNextActivity || !nextActivity?.next_activity_id) return;
    await onCompleteNextActivity(nextActivity.next_activity_id, null);
  };

  const handleNextActivitySuspend = (nextActivity) => {
    setSuspendTarget(nextActivity);
  };

  const handleSuspendConfirm = async (remark) => {
    if (!onSuspendNextActivity || !suspendTarget?.next_activity_id) return;
    setIsSavingSuspend(true);
    try {
      await onSuspendNextActivity(suspendTarget.next_activity_id, remark);
      setSuspendTarget(null);
    } finally {
      setIsSavingSuspend(false);
    }
  };

  const userTimezone = getUserTimezone();

  useEffect(() => {
    let cancelled = false;

    api
      .getMasterData("leadStatuses")
      .then((values) => {
        if (cancelled) return;
        const list = Array.isArray(values) ? values : [];
        setStatusOptions(list);
        setSelectableStatusOptions(list.filter((s) => !s.hideFromUser));
      })
      .catch(() => {
        if (!cancelled) {
          setStatusOptions([]);
          setSelectableStatusOptions([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Load source + rep options once for the dropdown prompts
  useEffect(() => {
    let cancelled = false;

    Promise.all([
      api.getMasterData("leadSources").catch(() => []),
      api.getSalesReps().catch(() => []),
    ]).then(([sources, reps]) => {
      if (cancelled) return;

      const sourceList = Array.isArray(sources) ? sources : [];
      const repList = Array.isArray(reps) ? reps : [];

      setSourceOptions(
        sourceList
          .filter((s) => !s?.hideFromUser && s?.name)
          .map((s) => ({ value: s.name, label: s.name })),
      );

      setRepOptions(
        repList.map((r) => ({
          value: String(r.user_id),
          label: `${r.full_name || "Rep"}${r.role ? ` (${r.role})` : ""}`,
        })),
      );
    });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!quickEditLeadId) return;

    const handleClickOutside = (event) => {
      if (!event.target.closest(".lead-status-cell")) {
        setQuickEditLeadId(null);
        setStatusError("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [quickEditLeadId]);

  useEffect(() => {
    if (!quickEditProductsLeadId) return;

    const handleProductsClickOutside = (event) => {
      if (!event.target.closest(".lead-products-cell")) {
        setQuickEditProductsLeadId(null);
        setProductsError("");
      }
    };

    document.addEventListener("mousedown", handleProductsClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleProductsClickOutside);
    };
  }, [quickEditProductsLeadId]);

  const openStatusQuickEdit = (lead, event) => {
    const button = event.currentTarget;
    const rect = button.getBoundingClientRect();

    setQuickEditPosition({
      top: rect.bottom + 6,
      left: Math.max(8, rect.left - 100),
      anchorTop: rect.top,
      anchorBottom: rect.bottom,
    });

    setQuickEditLeadId(lead.lead_id);
    setQuickEditStatus(lead.status || "");
    setStatusError("");
  };

  const openProductsQuickEdit = (lead, event) => {
    const button = event.currentTarget;
    const rect = button.getBoundingClientRect();

    setProductsQuickEditPosition({
      top: rect.bottom + 6,
      left: Math.max(8, rect.left - 100),
      anchorTop: rect.top,
      anchorBottom: rect.bottom,
    });

    setQuickEditProductsLeadId(lead.lead_id);
    setQuickEditProducts(normalizeList(lead.products));
    setProductsError("");
  };

  useEffect(() => {
    if (!quickEditLeadId || !quickEditPosition || !quickEditMenuRef.current) {
      return;
    }

    const menu = quickEditMenuRef.current;
    const menuRect = menu.getBoundingClientRect();

    const spaceBelow = window.innerHeight - quickEditPosition.anchorBottom;
    const spaceAbove = quickEditPosition.anchorTop;
    const gap = 6;

    if (spaceBelow >= menuRect.height + gap) {
      setQuickEditPosition((current) => ({
        ...current,
        top: current.anchorBottom + gap,
      }));
    } else if (spaceAbove >= menuRect.height + gap) {
      setQuickEditPosition((current) => ({
        ...current,
        top: current.anchorTop - menuRect.height - gap,
      }));
    }
  }, [
    quickEditLeadId,
    quickEditPosition?.anchorTop,
    quickEditPosition?.anchorBottom,
  ]);

  useEffect(() => {
    if (
      !quickEditProductsLeadId ||
      !productsQuickEditPosition ||
      !productsQuickEditMenuRef.current
    ) {
      return;
    }

    const menu = productsQuickEditMenuRef.current;
    const menuRect = menu.getBoundingClientRect();

    const gap = 6;
    const minTop = 90;
    const bottomMargin = 16;

    const spaceBelow =
      window.innerHeight - productsQuickEditPosition.anchorBottom;

    const spaceAbove = productsQuickEditPosition.anchorTop - minTop;

    if (spaceBelow >= menuRect.height + gap) {
      setProductsQuickEditPosition((current) => ({
        ...current,
        top: current.anchorBottom + gap,
      }));
    } else if (spaceAbove >= menuRect.height + gap) {
      setProductsQuickEditPosition((current) => ({
        ...current,
        top: Math.max(minTop, current.anchorTop - menuRect.height - gap),
      }));
    } else {
      const maxTop = Math.max(
        minTop,
        window.innerHeight - menuRect.height - bottomMargin,
      );

      setProductsQuickEditPosition((current) => ({
        ...current,
        top: Math.min(Math.max(minTop, current.anchorBottom + gap), maxTop),
      }));
    }
  }, [
    quickEditProductsLeadId,
    productsQuickEditPosition?.anchorTop,
    productsQuickEditPosition?.anchorBottom,
  ]);

  const handleStatusQuickSave = async (lead) => {
    if (!quickEditStatus || quickEditStatus === lead.status) {
      setQuickEditLeadId(null);
      return;
    }

    setIsSavingStatus(true);
    setStatusError("");

    try {
      const oldStatus = lead.status || "";
      await api.updateLead(lead.lead_id, {
        status: quickEditStatus,
      });

      try {
        await api.recordRecentLeadEdit?.({
          leadId: lead.lead_id,
          field: "status",
          oldValue: oldStatus,
          newValue: quickEditStatus,
          source: "inline",
        });
      } catch {}

      if (onLeadQuickUpdated) {
        onLeadQuickUpdated(lead.lead_id, quickEditStatus);
      }

      setQuickEditLeadId(null);
    } catch (err) {
      setStatusError(
        err?.response?.data?.message || "Failed to update status.",
      );
    } finally {
      setIsSavingStatus(false);
    }
  };

  const handleProductsQuickSave = async (lead) => {
    const currentProducts = normalizeList(lead.products);

    if (JSON.stringify(quickEditProducts) === JSON.stringify(currentProducts)) {
      setQuickEditProductsLeadId(null);
      return;
    }

    setIsSavingProducts(true);
    setProductsError("");

    try {
      const sanitizedProducts = (Array.isArray(quickEditProducts) ? quickEditProducts : [])
        .filter((p) => typeof p === "string" && p.trim().length > 0)
        .map((p) => p.trim());

      await api.updateLead(lead.lead_id, {
        products: sanitizedProducts,
      });

      try {
        await api.recordRecentLeadEdit?.({
          leadId: lead.lead_id,
          field: "products",
          oldValue: JSON.stringify(currentProducts),
          newValue: JSON.stringify(quickEditProducts),
          source: "inline",
        });
      } catch {}

      if (onLeadProductsQuickUpdated) {
        onLeadProductsQuickUpdated(lead.lead_id, sanitizedProducts);
      }

      setQuickEditProductsLeadId(null);
    } catch (err) {
      setProductsError(
        err?.response?.data?.message || "Failed to update products.",
      );
    } finally {
      setIsSavingProducts(false);
    }
  };

  if (!Array.isArray(leads) || leads.length === 0) {
    return (
      <div className="empty-state partner-empty-state">
        <i className="fas fa-inbox"></i>
        <h3>No leads found</h3>
        <p>Try changing your filters or add a new lead.</p>
      </div>
    );
  }

  const show = (column) => visibleColumns[column] !== false;
  const ownerCanSeeSalesRep = [
    "Admin",
    "SM",
    "Sales Owner",
    "Sales Manager",
  ].includes(String(userRole || "").trim());

  const groupedProductCatalog = productCatalog.reduce((groups, product) => {
    const value = product?.name || product?.product_name;
    if (!value) return groups;
    const category = product?.category || product?.category_name || "Other";
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(product);
    return groups;
  }, {});

  const normalizeList = (value) => {
    if (Array.isArray(value))
      return value.filter(
        (v) => v !== null && v !== undefined && String(v).trim(),
      );
    if (typeof value === "string" && value.trim()) {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [value];
      } catch {
        return [value];
      }
    }
    return [];
  };

  const openOverlay = (title, items, lead) => {
    if (title === "Products" && lead) {
      setOverlay({
        title: "Products",
        items: items,
        lead: lead,
        type: "products",
      });
    } else {
      setOverlay({ title, items, type: "list" });
    }
  };

  const getProductCategory = (productName) => {
    if (!productCatalog || !Array.isArray(productCatalog)) return "";
    const found = productCatalog.find(
      (p) => p.name === productName || p.product_name === productName,
    );
    return found?.category || found?.category_name || "";
  };

  const allowedActivityTypes = [
    "Demo",
    "Email",
    "Follow-up",
    "Meeting",
    "Note Added",
    "Phone Call",
    "Status Changed",
  ];

  const cycleHeader = (key) => (e) => {
    if (!onSortChange) return;
    onSortChange(cycleSort(sortState, key, e.shiftKey));
  };

  const sortBadge = (key) => {
    const entry = sortState.find((s) => s.col === key);
    if (!entry) {
      return (
        <span className="r6-sort-badge r6-sort-badge-idle" aria-hidden="true">
          ↕
        </span>
      );
    }
    const idx = sortState.indexOf(entry);
    return (
      <span className="r6-sort-badge">
        {entry.dir === "asc" ? "▲" : "▼"}
        {idx > 0 ? ` ${idx + 1}` : ""}
      </span>
    );
  };

  return (
    <>
      <div className="partner-table-wrapper">
        <table className="partner-leads-table">
          <thead>
            <tr>
              {show("dealership") && (
                <th
                  onClick={cycleHeader("dealership")}
                  style={{ cursor: "pointer" }}
                >
                  Dealership {sortBadge("dealership")}
                </th>
              )}
              {show("salesRep") && ownerCanSeeSalesRep && (
                <th
                  onClick={cycleHeader("sales_rep")}
                  style={{ cursor: "pointer" }}
                >
                  Sales Rep {sortBadge("sales_rep")}
                </th>
              )}
              {show("contact") && (
                <th
                  onClick={cycleHeader("contact")}
                  style={{ cursor: "pointer" }}
                >
                  Contact {sortBadge("contact")}
                </th>
              )}
              {show("status") && (
                <th
                  onClick={cycleHeader("status")}
                  style={{ cursor: "pointer" }}
                >
                  Status {sortBadge("status")}
                </th>
              )}
              {show("products") && <th>Products</th>}
              {show("activity") && (
                <th
                  onClick={cycleHeader("last_activity")}
                  style={{ cursor: "pointer" }}
                >
                  Activity {sortBadge("last_activity")}
                </th>
              )}
              {show("lastContact") && <th>Last Contact</th>}
              {show("nextContact") && (
                <th
                  onClick={cycleHeader("next_contact_date")}
                  style={{ cursor: "pointer" }}
                >
                  Next Contact {sortBadge("next_contact_date")}
                </th>
              )}
              {show("demo") && (
                <th
                  onClick={cycleHeader("demo_date")}
                  style={{ cursor: "pointer" }}
                >
                  Demo {sortBadge("demo_date")}
                </th>
              )}
              <th className="actions-column">Actions</th>
            </tr>
          </thead>
          <tbody>
            {leads.map((lead) => {
              const products = normalizeList(lead.products);
              const latestActivity = Array.isArray(lead.activities)
                ? lead.activities[0] || null
                : null;
              const demoStatus =
                lead.demo_status || lead.demoStatus || "Not Booked";
              const demoDate = lead.demo_date || lead.demoDate || "";
              const demoTime = lead.demo_time || lead.demoTime || "";
              const demoDateTime =
                demoDate || demoTime
                  ? formatUtcDateTimeParts(demoDate, demoTime)
                  : "";
              const firstContact = Array.isArray(lead.contacts)
                ? lead.contacts[0]
                : null;
              const contactName =
                [
                  firstContact?.first_name || firstContact?.firstName,
                  firstContact?.last_name || firstContact?.lastName,
                ]
                  .filter(Boolean)
                  .join(" ") ||
                lead.contact_name ||
                "";
              const contactEmail =
                firstContact?.email || lead.contact_email || "";

              return (
                <tr
                  key={lead.lead_id}
                  data-lead-id={lead.lead_id}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setRowMenu({
                      open: true,
                      x: e.clientX,
                      y: e.clientY,
                      lead: { ...lead, __userRole: userRole },
                    });
                  }}
                  onTouchStart={(e) => {
                    const t = e.touches[0];
                    touchStartPosRef.current = { x: t.clientX, y: t.clientY };
                    longPressTimerRef.current = setTimeout(() => {
                      setRowMenu({
                        open: true,
                        x: window.innerWidth / 2,
                        y: window.innerHeight - 60,
                        lead: { ...lead, __userRole: userRole },
                      });
                      if (navigator.vibrate) navigator.vibrate(15);
                    }, 500);
                  }}
                  onTouchMove={(e) => {
                    const t = e.touches[0];
                    const dx = Math.abs(t.clientX - touchStartPosRef.current.x);
                    const dy = Math.abs(t.clientY - touchStartPosRef.current.y);
                    if (dx > 10 || dy > 10) {
                      clearTimeout(longPressTimerRef.current);
                    }
                  }}
                  onTouchEnd={() => clearTimeout(longPressTimerRef.current)}
                  onTouchCancel={() => clearTimeout(longPressTimerRef.current)}
                >
                  {show("dealership") && (
                    <td data-label="Dealership" className="lead-company-cell">
                      <button
                        type="button"
                        className="partner-lead-link"
                        onClick={() => onView(lead.lead_id)}
                      >
                        {lead.company || "Unnamed Dealership"}
                      </button>
                      {lead.recently_edited && (
                        <span
                          className="r6-amber-dot"
                          title={`${lead.recently_edited.user_full_name || "Someone"} edited ${
                            lead.recently_edited.field
                          } at ${new Date(
                            lead.recently_edited.updated_at,
                          ).toLocaleString()}`}
                        />
                      )}
                      {lead.email && (
                        <div className="lead-secondary-text">{lead.email}</div>
                      )}
                    </td>
                  )}
                  {show("salesRep") && ownerCanSeeSalesRep && (
                    <td data-label="Sales Rep">
                      <div className="sales-rep-cell">
                        <span className="sales-rep-avatar">
                          {getInitials(lead.partner_name)}
                        </span>
                        <span>
                          {lead.partner_name || lead.sales_rep_name || "N/A"}
                        </span>
                      </div>
                    </td>
                  )}
                  {show("contact") && (
                    <td data-label="Contact">
                      {contactName ? (
                        <>
                          <div>{contactName}</div>
                          {contactEmail && (
                            <div className="lead-secondary-text">
                              {contactEmail}
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="lead-secondary-text">N/A</span>
                      )}
                      <button
                        type="button"
                        className="r6-inline-pencil"
                        onClick={() => setQuickAddContactLead(lead)}
                        title="Add contact"
                      >
                        <i className="fas fa-user-plus" />
                      </button>
                    </td>
                  )}
                  {show("status") && (
                    <td data-label="Status" className="lead-status-table-cell">
                      <div className="lead-status-cell">
                        <LeadStatusBadge status={lead.status} />

                        <button
                          type="button"
                          className="lead-quick-edit-button"
                          onClick={(event) => openStatusQuickEdit(lead, event)}
                          title="Edit status"
                          aria-label={`Edit status for ${lead.lead_id}`}
                        >
                          <i className="fas fa-pencil-alt"></i>
                        </button>

                        {quickEditLeadId === lead.lead_id &&
                          quickEditPosition &&
                          createPortal(
                            <>
                              <div
                                className="lead-quick-edit-backdrop"
                                onMouseDown={() => {
                                  setQuickEditLeadId(null);
                                  setStatusError("");
                                }}
                              />

                              <div
                                ref={quickEditMenuRef}
                                className="lead-quick-edit-menu"
                                style={{
                                  position: "fixed",
                                  top: `${quickEditPosition.top}px`,
                                  left: `${quickEditPosition.left}px`,
                                }}
                                onMouseDown={(event) => event.stopPropagation()}
                              >
                                <div className="lead-quick-edit-title">
                                  Edit Status
                                </div>

                                <div className="lead-quick-edit-options">
                                  <select
                                    className="lead-quick-edit-select"
                                    value={quickEditStatus}
                                    onChange={(event) =>
                                      setQuickEditStatus(event.target.value)
                                    }
                                  >
                                    {quickEditStatus &&
                                      !selectableStatusOptions.some(
                                        (o) => o.name === quickEditStatus,
                                      ) && (
                                        <option value={quickEditStatus}>
                                          {quickEditStatus}
                                        </option>
                                      )}

                                    {selectableStatusOptions.map((option) => {
                                      const value = option?.name;
                                      if (!value) return null;
                                      return (
                                        <option key={value} value={value}>
                                          {value}
                                        </option>
                                      );
                                    })}
                                  </select>
                                </div>

                                {statusError && (
                                  <div className="lead-quick-edit-error">
                                    {statusError}
                                  </div>
                                )}

                                <div className="lead-quick-edit-actions">
                                  <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => setQuickEditLeadId(null)}
                                    disabled={isSavingStatus}
                                  >
                                    Cancel
                                  </button>

                                  <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    onClick={() => handleStatusQuickSave(lead)}
                                    disabled={
                                      isSavingStatus || !quickEditStatus
                                    }
                                  >
                                    {isSavingStatus ? "Saving..." : "Save"}
                                  </button>
                                </div>
                              </div>
                            </>,
                            document.body,
                          )}
                      </div>
                    </td>
                  )}
                  {show("products") && (
                    <td
                      data-label="Products"
                      className="lead-products-table-cell"
                    >
                      <div className="lead-products-cell">
                        {products.length ? (
                          <div className="partner-table-tags">
                            {products.slice(0, 1).map((product, i) => (
                              <span
                                className="partner-table-tag lead-product-name"
                                key={`${product}-${i}`}
                                title={product}
                              >
                                {product}
                              </span>
                            ))}

                            {products.length > 1 && (
                              <button
                                type="button"
                                className="partner-table-more"
                                onClick={() =>
                                  openOverlay("Products", products, lead)
                                }
                                title="View all products"
                              >
                                +{products.length - 1}
                              </button>
                            )}
                          </div>
                        ) : (
                          <span className="lead-secondary-text">None</span>
                        )}

                        <button
                          type="button"
                          className="lead-quick-edit-button"
                          onClick={(event) =>
                            openProductsQuickEdit(lead, event)
                          }
                          title="Edit products"
                          aria-label={`Edit products for ${lead.lead_id}`}
                        >
                          <i className="fas fa-pencil-alt"></i>
                        </button>
                        {quickEditProductsLeadId === lead.lead_id &&
                          productsQuickEditPosition &&
                          createPortal(
                            <>
                              <div
                                className="lead-quick-edit-backdrop"
                                onMouseDown={() => {
                                  setQuickEditProductsLeadId(null);
                                  setProductsError("");
                                }}
                              />

                              <div
                                ref={productsQuickEditMenuRef}
                                className="lead-quick-edit-menu lead-products-quick-edit-menu"
                                style={{
                                  position: "fixed",
                                  top: `${productsQuickEditPosition.top}px`,
                                  left: `${productsQuickEditPosition.left}px`,
                                }}
                                onMouseDown={(event) => event.stopPropagation()}
                              >
                                <div className="lead-products-quick-edit-header">
                                  <div className="lead-quick-edit-title">
                                    Edit Products
                                    {quickEditProducts.length > 0 && (
                                      <span className="lead-products-selected-count">
                                        {quickEditProducts.length} selected
                                      </span>
                                    )}
                                  </div>

                                  {quickEditProducts.length > 0 && (
                                    <button
                                      type="button"
                                      className="lead-products-quick-edit-clear"
                                      onClick={() => setQuickEditProducts([])}
                                    >
                                      Clear
                                    </button>
                                  )}
                                </div>

                                <div className="lead-products-quick-edit-list">
                                  {Object.entries(groupedProductCatalog).map(
                                    ([category, products]) => (
                                      <div
                                        key={category}
                                        className="product-category-group"
                                      >
                                        <div className="product-category-label">
                                          {category}
                                        </div>

                                        <div className="product-category-products">
                                          {products.map((product) => {
                                            const value =
                                              product?.name ||
                                              product?.product_name;

                                            const checked =
                                              quickEditProducts.includes(value);

                                            return (
                                              <label
                                                key={value}
                                                className="filter-checkbox-option"
                                              >
                                                <input
                                                  type="checkbox"
                                                  checked={checked}
                                                  onChange={(event) => {
                                                    setQuickEditProducts(
                                                      (current) =>
                                                        event.target.checked
                                                          ? [...current, value]
                                                          : current.filter(
                                                              (item) =>
                                                                item !== value,
                                                            ),
                                                    );
                                                  }}
                                                />

                                                <span>{value}</span>

                                                {product?.subcategory && (
                                                  <small>
                                                    {product.subcategory}
                                                  </small>
                                                )}
                                              </label>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    ),
                                  )}
                                </div>

                                {productsError && (
                                  <div className="lead-quick-edit-error">
                                    {productsError}
                                  </div>
                                )}

                                <div className="lead-quick-edit-actions">
                                  <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    onClick={() =>
                                      setQuickEditProductsLeadId(null)
                                    }
                                    disabled={isSavingProducts}
                                  >
                                    Cancel
                                  </button>

                                  <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    onClick={() =>
                                      handleProductsQuickSave(lead)
                                    }
                                    disabled={isSavingProducts}
                                  >
                                    {isSavingProducts ? "Saving..." : "Save"}
                                  </button>
                                </div>
                              </div>
                            </>,
                            document.body,
                          )}
                      </div>
                    </td>
                  )}
                  {show("activity") && (
                    <td data-label="Activity">
                      {latestActivity?.type ? (
                        <span>{latestActivity.type}</span>
                      ) : (
                        <span className="lead-secondary-text">N/A</span>
                      )}
                    </td>
                  )}

                  {show("lastContact") && (
                    <td data-label="Last Contact">
                      {latestActivity?.scheduled_date ? (
                        <span>
                          {formatDateInUserTimezone(
                            latestActivity.scheduled_date,
                            userTimezone,
                          )}
                        </span>
                      ) : (
                        <span className="lead-secondary-text">N/A</span>
                      )}
                    </td>
                  )}
                  {show("nextContact") && (
                    <td data-label="Next Contact">
                      <NextActivityCell
                        activity={lead.active_next_activity || null}
                        onAdd={() => openAddNextActivity(lead.lead_id)}
                        onComplete={() =>
                          handleNextActivityComplete(
                            lead,
                            lead.active_next_activity,
                          )
                        }
                        onSuspend={() =>
                          handleNextActivitySuspend(lead.active_next_activity)
                        }
                      />
                    </td>
                  )}
                  {show("demo") && (
                    <td data-label="Demo">
                      {demoStatus === "Not Booked" ? (
                        <button
                          type="button"
                          className="book-demo-button lead-table-book-demo"
                          onClick={() => onBookDemo(lead.lead_id)}
                          title="Book Demo"
                        >
                          Book demo
                        </button>
                      ) : (
                        <>
                          <span
                            className={`demo-status-${String(demoStatus)
                              .toLowerCase()
                              .replace(/\s+/g, "-")}`}
                          >
                            {demoStatus}
                          </span>

                          {demoDateTime && (
                            <div className="lead-secondary-text">
                              {demoDateTime}
                            </div>
                          )}
                        </>
                      )}
                    </td>
                  )}
                  <td data-label="Actions" className="actions">
                    <div className="partner-row-actions">
                      <button
                        type="button"
                        className="partner-icon-action"
                        onClick={() => onEdit(lead.lead_id)}
                        title="Edit Lead"
                        aria-label="Edit Lead"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        type="button"
                        className="partner-icon-action"
                        onClick={() => setActivityLeadId(lead.lead_id)}
                        title="Add Activity"
                        aria-label="Add Activity"
                      >
                        <i className="fas fa-plus-circle"></i>
                      </button>
                      <button
                        type="button"
                        className="partner-icon-action r6-more-btn"
                        onClick={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setRowMenu({
                            open: true,
                            x: rect.right - 200,
                            y: rect.bottom + 6,
                            lead: { ...lead, __userRole: userRole },
                          });
                        }}
                        title="More actions"
                        aria-label="More actions"
                      >
                        <i className="fas fa-ellipsis-h"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {overlay && (
        <div
          className="modal-overlay"
          role="presentation"
          onMouseDown={() => setOverlay(null)}
        >
          <div
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-label={overlay.title}
            style={{
              maxWidth: overlay.type === "products" ? "480px" : "520px",
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>{overlay.title}</h3>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setOverlay(null)}
                aria-label="Close"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              {overlay.type === "products" ? (
                <div className="products-modal-grid">
                  {overlay.items.map((item, i) => {
                    const category = getProductCategory(item);
                    return (
                      <div key={`${item}-${i}`} className="product-modal-item">
                        <span className="product-modal-name">{item}</span>
                        {category && (
                          <span className="product-modal-category">
                            {category}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <ul className="modal-list">
                  {overlay.items.map((item, i) => (
                    <li key={`${item}-${i}`}>{item}</li>
                  ))}
                </ul>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setOverlay(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {activityLeadId &&
        createPortal(
          <div
            className="modal-overlay"
            role="presentation"
            onMouseDown={() => setActivityLeadId(null)}
          >
            <div
              className="modal"
              role="dialog"
              aria-modal="true"
              aria-label="Add Activity"
              onMouseDown={(event) => event.stopPropagation()}
            >
              <div className="modal-header">
                <h3>Add Activity</h3>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setActivityLeadId(null)}
                  aria-label="Close"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <div className="modal-body">
                <AddActivity
                  onSave={async (activityData) => {
                    setIsSavingActivity(true);

                    try {
                      await onAddActivity(activityLeadId, activityData);
                      setActivityLeadId(null);
                    } finally {
                      setIsSavingActivity(false);
                    }
                  }}
                  onCancel={() => setActivityLeadId(null)}
                  saving={isSavingActivity}
                />
              </div>
            </div>
          </div>,
          document.body,
        )}

      {nextActivityLeadId &&
        createPortal(
          <div
            className="modal-overlay"
            role="presentation"
            onMouseDown={() =>
              !isSavingNextActivity && setNextActivityLeadId(null)
            }
          >
            <div
              className="modal"
              role="dialog"
              aria-modal="true"
              aria-label="Add Next Contact"
              onMouseDown={(event) => event.stopPropagation()}
              style={{ maxWidth: 620 }}
            >
              <div className="modal-header">
                <h3>Add Next Contact</h3>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setNextActivityLeadId(null)}
                  aria-label="Close"
                  disabled={isSavingNextActivity}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <div className="modal-body">
                <AddNextActivity
                  previousActive={(() => {
                    const row = leads.find(
                      (l) => String(l.lead_id) === String(nextActivityLeadId),
                    );
                    return row?.active_next_activity || null;
                  })()}
                  onSave={handleNextActivitySave}
                  onCancel={() => setNextActivityLeadId(null)}
                  saving={isSavingNextActivity}
                />
              </div>
            </div>
          </div>,
          document.body,
        )}

      {suspendTarget && (
        <SuspendPrompt
          activity={suspendTarget}
          onConfirm={handleSuspendConfirm}
          onCancel={() => !isSavingSuspend && setSuspendTarget(null)}
          saving={isSavingSuspend}
        />
      )}

      <RowContextMenu
        open={rowMenu.open}
        x={rowMenu.x}
        y={rowMenu.y}
        lead={rowMenu.lead}
        onClose={() => setRowMenu({ open: false, x: 0, y: 0, lead: null })}
        onAction={async (key, lead) => {
          setRowMenu({ open: false, x: 0, y: 0, lead: null });

          if (key === "open") {
            onView?.(lead.lead_id);
            return;
          }
          if (key === "edit") {
            onEdit?.(lead.lead_id);
            return;
          }
          if (key === "activity") {
            setActivityLeadId(lead.lead_id);
            return;
          }
          if (key === "next") {
            openAddNextActivity(lead.lead_id);
            return;
          }
          if (key === "nextComplete") {
            if (
              lead.active_next_activity?.next_activity_id &&
              typeof onCompleteNextActivity === "function"
            ) {
              await onCompleteNextActivity(
                lead.active_next_activity.next_activity_id,
                null,
              );
            }
            return;
          }
          if (key === "nextSuspend") {
            if (lead.active_next_activity) {
              handleNextActivitySuspend(lead.active_next_activity);
            }
            return;
          }

          if (key === "products") {
            const tr = document.querySelector(
              `.partner-leads-table tbody tr[data-lead-id="${lead.lead_id}"]`,
            );
            const btn = tr?.querySelector(
              ".lead-products-cell .lead-quick-edit-button",
            );
            if (btn) btn.click();
            return;
          }

          if (key === "status") {
            const tr = document.querySelector(
              `.partner-leads-table tbody tr[data-lead-id="${lead.lead_id}"]`,
            );
            const btn = tr?.querySelector(
              ".lead-status-cell .lead-quick-edit-button",
            );
            if (btn) btn.click();
            return;
          }

          if (key === "source") {
            setPrompt({ open: true, type: "source", lead, saving: false });
            return;
          }

          if (key === "assign") {
            setPrompt({ open: true, type: "assign", lead, saving: false });
            return;
          }

          if (key === "delete") {
            if (
              !window.confirm(
                `Delete "${lead.company}"? This cannot be undone.`,
              )
            ) {
              return;
            }
            try {
              await api.deleteLead(lead.lead_id);
              window.location.reload();
            } catch (err) {
              window.alert(
                err?.response?.data?.error || "Failed to delete lead",
              );
            }
            return;
          }
        }}
      />

      {/* Dropdown modal for Edit Source / Assign Rep */}
      <RowActionPrompt
        open={prompt.open}
        title={
          prompt.type === "assign" ? "Assign to Rep" : "Edit Source"
        }
        options={prompt.type === "assign" ? repOptions : sourceOptions}
        value={
          prompt.type === "assign"
            ? String(prompt.lead?.partner_id || "")
            : prompt.lead?.lead_source || ""
        }
        saving={prompt.saving}
        onSave={async (value) => {
          if (!prompt.lead || !value) return;
          setPrompt((p) => ({ ...p, saving: true }));
          try {
            if (prompt.type === "source") {
              await api.updateLead(prompt.lead.lead_id, {
                leadSource: value,
              });
            } else if (prompt.type === "assign") {
              await api.updateLead(prompt.lead.lead_id, {
                salesRepId: Number(value),
              });
            }
            setPrompt({ open: false, type: null, lead: null, saving: false });
            window.location.reload();
          } catch (err) {
            window.alert(
              err?.response?.data?.error || "Failed to save",
            );
            setPrompt((p) => ({ ...p, saving: false }));
          }
        }}
        onClose={() =>
          !prompt.saving &&
          setPrompt({ open: false, type: null, lead: null, saving: false })
        }
      />

      {quickAddContactLead && (
        <QuickAddContactModal
          lead={quickAddContactLead}
          onClose={() => setQuickAddContactLead(null)}
          onCreated={() => {
            onLeadQuickUpdated?.(
              quickAddContactLead.lead_id,
              quickAddContactLead.status,
            );
            setQuickAddContactLead(null);
          }}
        />
      )}
    </>
  );
}

function getInitials(name) {
  if (!name) return "?";
  return String(name)
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
}

export default LeadTable;
